// ============================================================================
// Géométrie déductive du noyau (lot 5.4) : la FIGURE de référence.
// ============================================================================
// Les coordonnées d'une figure qui réalise l'énoncé. Elles ne servent jamais à
// JUSTIFIER une ligne (seule une propriété du cours le fait) ; elles disent si une
// proposition est VRAIE : une ligne fausse sur la figure est refusée tout de suite
// (« C'est faux »), une ligne vraie mais non justifiée attend sa propriété
// (« D'où vient-elle ? »). Elles empêchent aussi d'accepter un quadrilatère croisé.
// ============================================================================

import type { GProp, LineRef, Pt } from './props';

export interface FigureSpec {
  /** coordonnées des points : « A: [0, 0] » */
  points: Readonly<Record<string, readonly [number, number]>>;
  /** droites nommées : deux points de passage « d₁: [[0, 0], [1, 2]] » */
  lines?: Readonly<Record<string, readonly [readonly [number, number], readonly [number, number]]>>;
}

type V2 = [number, number];
const vsub = (a: V2, b: V2): V2 => [a[0] - b[0], a[1] - b[1]];
const dot = (a: V2, b: V2): number => a[0] * b[0] + a[1] * b[1];
const cross = (a: V2, b: V2): number => a[0] * b[1] - a[1] * b[0];
const norm = (a: V2): number => Math.hypot(a[0], a[1]);

export class Figure {
  readonly scale: number;
  constructor(readonly spec: FigureSpec) {
    let m = 1;
    for (const [x, y] of Object.values(spec.points)) m = Math.max(m, Math.abs(x), Math.abs(y));
    this.scale = m;
  }
  has(p: Pt): boolean {
    return p in this.spec.points;
  }
  pt(p: Pt): V2 {
    const c = this.spec.points[p];
    if (!c) throw new Error(`point ${p} absent de la figure`);
    return [c[0], c[1]];
  }
  /** un point et un vecteur directeur de la droite */
  line(l: LineRef): { o: V2; u: V2 } {
    if (l.k === 'pts') {
      const o = this.pt(l.a);
      return { o, u: vsub(this.pt(l.b), o) };
    }
    const d = this.spec.lines?.[l.n];
    if (!d) throw new Error(`droite (${l.n}) absente de la figure`);
    const o: V2 = [d[0][0], d[0][1]];
    return { o, u: vsub([d[1][0], d[1][1]], o) };
  }
  dist(a: Pt, b: Pt): number {
    return norm(vsub(this.pt(a), this.pt(b)));
  }
  /** longueurs de tous les segments (clés triées « AB ») */
  lengths(): Record<string, number> {
    const names = Object.keys(this.spec.points).sort();
    const out: Record<string, number> = {};
    for (let i = 0; i < names.length; i++)
      for (let j = i + 1; j < names.length; j++)
        out[names[i]! + names[j]!] = this.dist(names[i]!, names[j]!);
    return out;
  }

  private zeroLen(x: number): boolean {
    return Math.abs(x) <= 1e-7 * this.scale;
  }
  private same(a: V2, b: V2): boolean {
    return this.zeroLen(norm(vsub(a, b)));
  }
  private parallel(u: V2, v: V2): boolean {
    const nu = norm(u);
    const nv = norm(v);
    return nu > 0 && nv > 0 && Math.abs(cross(u, v)) <= 1e-7 * nu * nv;
  }
  private orthogonal(u: V2, v: V2): boolean {
    const nu = norm(u);
    const nv = norm(v);
    return nu > 0 && nv > 0 && Math.abs(dot(u, v)) <= 1e-7 * nu * nv;
  }
  private onLine(p: V2, o: V2, u: V2): boolean {
    const w = vsub(p, o);
    return norm(w) <= 1e-7 * this.scale || this.parallel(w, u);
  }
  private collinear(a: Pt, b: Pt, c: Pt): boolean {
    const A = this.pt(a);
    return this.onLine(this.pt(c), A, vsub(this.pt(b), A));
  }
  private rightAt(at: Pt, b: Pt, c: Pt): boolean {
    const A = this.pt(at);
    return this.orthogonal(vsub(this.pt(b), A), vsub(this.pt(c), A));
  }
  private parallelogram(v: Pt[]): boolean {
    const [A, B, C, D] = v.map((p) => this.pt(p)) as [V2, V2, V2, V2];
    return (
      this.same([A[0] + C[0], A[1] + C[1]], [B[0] + D[0], B[1] + D[1]]) &&
      !this.collinear(v[0]!, v[1]!, v[2]!)
    );
  }
  private eqLen(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
    const x = this.dist(a, b);
    const y = this.dist(c, d);
    return Math.abs(x - y) <= 1e-7 * Math.max(1, x, y);
  }

  /** La proposition est-elle vraie sur la figure ? */
  holds(p: GProp): boolean {
    switch (p.k) {
      case 'par':
      case 'npar': {
        const par = this.parallel(this.line(p.a).u, this.line(p.b).u);
        return p.k === 'par' ? par : !par;
      }
      case 'perp':
        return this.orthogonal(this.line(p.a).u, this.line(p.b).u);
      case 'mid': {
        const A = this.pt(p.a);
        const B = this.pt(p.b);
        return this.same(this.pt(p.p), [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2]);
      }
      case 'onseg': {
        if (!this.collinear(p.a, p.b, p.p)) return false;
        const A = this.pt(p.a);
        const u = vsub(this.pt(p.b), A);
        const t = dot(vsub(this.pt(p.p), A), u) / dot(u, u);
        return t >= -1e-9 && t <= 1 + 1e-9;
      }
      case 'online': {
        const { o, u } = this.line(p.l);
        return this.onLine(this.pt(p.p), o, u);
      }
      case 'bis':
        return this.eqLen(p.p, p.a, p.p, p.b);
      case 'circ':
        return this.rightAt(p.p, p.a, p.b);
      case 'para':
        return this.parallelogram(p.v);
      case 'rectangle':
        return this.parallelogram(p.v) && this.rightAt(p.v[1]!, p.v[0]!, p.v[2]!);
      case 'losange':
        return this.parallelogram(p.v) && this.eqLen(p.v[0]!, p.v[1]!, p.v[1]!, p.v[2]!);
      case 'carre':
        return (
          this.parallelogram(p.v) &&
          this.rightAt(p.v[1]!, p.v[0]!, p.v[2]!) &&
          this.eqLen(p.v[0]!, p.v[1]!, p.v[1]!, p.v[2]!)
        );
      case 'rt':
        return !this.collinear(p.at, p.b, p.c) && this.rightAt(p.at, p.b, p.c);
      case 'iso':
        return !this.collinear(p.at, p.b, p.c) && this.eqLen(p.at, p.b, p.at, p.c);
      case 'nrt': {
        const [a, b, c] = p.v as [Pt, Pt, Pt];
        return (
          !this.collinear(a, b, c) &&
          !this.rightAt(a, b, c) &&
          !this.rightAt(b, a, c) &&
          !this.rightAt(c, a, b)
        );
      }
    }
  }
}
